import { logger } from '../../core/utils/logger';

const STATIC_CACHE_NAME = 'nu-data-ui-static-v1';
const DYNAMIC_CACHE_NAME = 'nu-data-ui-dynamic-v1';

const STATIC_ASSETS = [
  '/',
  '/static/js/bundle.js',
  '/static/css/main.css',
  '/manifest.json'
];

const API_CACHE_PATTERNS = [
  /\/api\/admin\/users/,
  /\/api\/admin\/audit-logs/,
  /\/api\/admin\/billing/,
  /\/api\/admin\/monitoring/
];



class ServiceWorkerCache {
  private static instance: ServiceWorkerCache;

  static getInstance(): ServiceWorkerCache {
    if (!ServiceWorkerCache.instance) {
      ServiceWorkerCache.instance = new ServiceWorkerCache();
    }
    return ServiceWorkerCache.instance;
  }

  async install(): Promise<void> {
    try {
      const cache = await caches.open(STATIC_CACHE_NAME);
      await cache.addAll(STATIC_ASSETS);
      logger.info('[SW] Static assets cached successfully');
    } catch (error) {
      logger.error('[SW] Failed to cache static assets:', error);
    }
  }

  async handleFetch(request: Request): Promise<Response> {
    const url = new URL(request.url);
    
    if (this.isStaticAsset(url)) {
      return this.cacheFirst(request);
    }
    
    if (this.isCacheableApiRequest(url)) {
      return this.staleWhileRevalidate(request);
    }
    
    if (this.isApiRequest(url)) {
      return this.networkFirst(request);
    }
    
    return fetch(request);
  }

  private isStaticAsset(url: URL): boolean {
    return url.pathname.startsWith('/static/') || 
           url.pathname.endsWith('.js') || 
           url.pathname.endsWith('.css') ||
           url.pathname.endsWith('.png') ||
           url.pathname.endsWith('.jpg') ||
           url.pathname.endsWith('.svg');
  }

  private isApiRequest(url: URL): boolean {
    return url.pathname.startsWith('/api/');
  }

  private isCacheableApiRequest(url: URL): boolean {
    return API_CACHE_PATTERNS.some(pattern => pattern.test(url.pathname));
  }

  private async cacheFirst(request: Request): Promise<Response> {
    try {
      const cache = await caches.open(STATIC_CACHE_NAME);
      const cachedResponse = await cache.match(request);
      
      if (cachedResponse) {
        return cachedResponse;
      }
      
      const networkResponse = await fetch(request);
      if (networkResponse.ok) {
        await cache.put(request, networkResponse.clone());
      }
      
      return networkResponse;
    } catch (error) {
      logger.error('[SW] Cache first strategy failed:', error);
      return fetch(request);
    }
  }

  private async networkFirst(request: Request): Promise<Response> {
    try {
      const networkResponse = await fetch(request);
      
      if (networkResponse.ok && this.isCacheableApiRequest(new URL(request.url))) {
        const cache = await caches.open(DYNAMIC_CACHE_NAME);
        await cache.put(request, networkResponse.clone());
      }
      
      return networkResponse;
    } catch (error) {
      logger.error('[SW] Network first strategy failed, trying cache:', error);
      
      const cache = await caches.open(DYNAMIC_CACHE_NAME);
      const cachedResponse = await cache.match(request);
      
      if (cachedResponse) {
        return cachedResponse;
      }
      
      throw error;
    }
  }

  private async staleWhileRevalidate(request: Request): Promise<Response> {
    const cache = await caches.open(DYNAMIC_CACHE_NAME);
    const cachedResponse = await cache.match(request);
    
    const fetchPromise = fetch(request).then(networkResponse => {
      if (networkResponse.ok) {
        cache.put(request, networkResponse.clone());
      }
      return networkResponse;
    }).catch(error => {
      logger.error('[SW] Stale while revalidate network fetch failed:', error);
      return cachedResponse || Response.error();
    });
    
    return cachedResponse || fetchPromise;
  }

  async clearOldCaches(): Promise<void> {
    const cacheNames = await caches.keys();
    const oldCaches = cacheNames.filter(name => 
      name !== STATIC_CACHE_NAME && 
      name !== DYNAMIC_CACHE_NAME &&
      name.startsWith('nu-data-ui-')
    );
    
    await Promise.all(
      oldCaches.map(cacheName => caches.delete(cacheName))
    );
    
    logger.info('[SW] Old caches cleared:', oldCaches);
  }

  async getCacheStats(): Promise<{ static: number; dynamic: number }> {
    const staticCache = await caches.open(STATIC_CACHE_NAME);
    const dynamicCache = await caches.open(DYNAMIC_CACHE_NAME);
    
    const staticKeys = await staticCache.keys();
    const dynamicKeys = await dynamicCache.keys();
    
    return {
      static: staticKeys.length,
      dynamic: dynamicKeys.length
    };
  }

  async preloadCriticalResources(urls: string[]): Promise<void> {
    const cache = await caches.open(STATIC_CACHE_NAME);
    
    const preloadPromises = urls.map(async (url) => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          await cache.put(url, response);
        }
      } catch (error) {
        logger.warn(`[SW] Failed to preload ${url}:`, error);
      }
    });
    
    await Promise.all(preloadPromises);
  }
}

export const serviceWorkerCache = ServiceWorkerCache.getInstance();

export const registerServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      logger.info('[SW] Service worker registered successfully:', registration);
      
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.info('[SW] New service worker available, consider refreshing');
              window.dispatchEvent(new CustomEvent('sw-update-available'));
            }
          });
        }
      });
      
    } catch (error) {
      logger.error('[SW] Service worker registration failed:', error);
    }
  }
};

export const unregisterServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      logger.info('[SW] Service worker unregistered successfully');
    } catch (error) {
      logger.error('[SW] Service worker unregistration failed:', error);
    }
  }
};
